import React from 'react';
import { Box, VStack, Text } from '@chakra-ui/react';
import { Scanner } from '@yudiel/react-qr-scanner';
import { useQRScanner } from '@/hooks/payments/useQRScanner';
import { useCameraSettings } from '@/hooks/payments/useCameraSettings';
import { Button } from '@/components/ui/button';

interface QRScanContentProps {
  onScan?: (result: string) => void;
  onClose?: () => void;
  isOpen?: boolean;
  onResetScanned?: (resetFn: () => void) => void;
}

export const QRScanContent: React.FC<QRScanContentProps> = ({
  onScan,
  onClose,
  isOpen,
  onResetScanned,
}) => {
  const { error, scanned, resetScanned, handleScan, handleError } = useQRScanner({
    onScan,
    onClose,
    isOpen,
  });
  const { openSettings, shouldShowSettingsButton } = useCameraSettings();

  // resetScanned関数を親コンポーネントに渡す
  React.useEffect(() => {
    if (onResetScanned) {
      onResetScanned(resetScanned);
    }
  }, [onResetScanned, resetScanned]);

  const showSettingsButton = shouldShowSettingsButton(!!error);

  return (
    <VStack gap={4} h="full" justify="center" align="center" userSelect="none">
      <Box
        w="full"
        maxW="400px"
        h="400px"
        borderRadius="xl"
        overflow="hidden"
        position="relative"
        bg="black"
      >
        {error ? (
          <VStack h="full" justify="center" align="center" p={4} gap={4}>
            <Text color="red.500" fontSize="sm" textAlign="center">
              {error}
            </Text>
            <Text color="gray.400" fontSize="xs" textAlign="center">
              ブラウザの設定からカメラへのアクセスを許可してください
            </Text>
            {showSettingsButton && (
              <Button colorScheme="blue" size="sm" onClick={openSettings} mt={2}>
                設定方法を確認
              </Button>
            )}
          </VStack>
        ) : (
          isOpen && (
            <Box position="relative" w="full" h="full">
              <Scanner
                sound={false}
                onScan={result => {
                  const text = result[0]?.rawValue;
                  if (text) {
                    handleScan(text);
                  }
                }}
                onError={handleError}
                constraints={{
                  facingMode: 'environment',
                }}
                styles={{
                  container: {
                    width: '100%',
                    height: '100%',
                  },
                  video: {
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  },
                }}
                components={{
                  finder: false,
                }}
                scanDelay={500}
                allowMultiple={false}
                classNames={{
                  container: 'qr-scanner-container',
                  video: 'qr-scanner-video',
                }}
              />
              {/* カスタムオーバーレイ - 枠外を薄暗く、四隅に枠線 */}
              <Box
                position="absolute"
                top="0"
                left="0"
                right="0"
                bottom="0"
                pointerEvents="none"
                zIndex={1}
              >
                {/* 暗いオーバーレイ */}
                <Box
                  position="absolute"
                  top="50%"
                  left="50%"
                  transform="translate(-50%, -50%)"
                  w="70%"
                  h="70%"
                  maxW="280px"
                  maxH="280px"
                  boxShadow="0 0 0 9999px rgba(0, 0, 0, 0.6)"
                />

                {/* 四隅の枠線 */}
                <Box
                  position="absolute"
                  top="50%"
                  left="50%"
                  transform="translate(-50%, -50%)"
                  w="calc(70% + 12px)"
                  h="calc(70% + 12px)"
                  maxW="calc(280px + 12px)"
                  maxH="calc(280px + 12px)"
                >
                  {/* 左上 */}
                  <Box
                    position="absolute"
                    top="0px"
                    left="0px"
                    w="50px"
                    h="50px"
                    borderTop="6px solid"
                    borderLeft="6px solid"
                    borderColor="teal.500"
                    borderTopLeftRadius="lg"
                  />
                  {/* 右上 */}
                  <Box
                    position="absolute"
                    top="0px"
                    right="0px"
                    w="50px"
                    h="50px"
                    borderTop="6px solid"
                    borderRight="6px solid"
                    borderColor="teal.500"
                    borderTopRightRadius="lg"
                  />
                  {/* 左下 */}
                  <Box
                    position="absolute"
                    bottom="0px"
                    left="0px"
                    w="50px"
                    h="50px"
                    borderBottom="6px solid"
                    borderLeft="6px solid"
                    borderColor="teal.500"
                    borderBottomLeftRadius="lg"
                  />
                  {/* 右下 */}
                  <Box
                    position="absolute"
                    bottom="0px"
                    right="0px"
                    w="50px"
                    h="50px"
                    borderBottom="6px solid"
                    borderRight="6px solid"
                    borderColor="teal.500"
                    borderBottomRightRadius="lg"
                  />
                </Box>
              </Box>
            </Box>
          )
        )}
      </Box>

      <Text fontSize="sm" color="gray.600" mt={4} textAlign="center">
        {scanned ? '読み取り成功！' : '店舗のQRコードをカメラに向けてください'}
      </Text>
    </VStack>
  );
};
